/**
 * The field comparison for one company, fetched once when somebody looks.
 *
 * Deliberately not part of `useBrain`. That hook POLLS while a run is in
 * flight, and this answer costs the server three queries that have nothing to
 * do with the run's progress - polling it would multiply the cost of watching a
 * crawl by the size of the register.
 *
 * ⚠ NOT LOADED AND NOTHING TO SAY RENDER THE SAME, AND THAT IS SAFE HERE. The
 * comparison is additive: a findings list without it is complete and correct,
 * so an empty result during the first moments is not a claim about anything.
 * The rule this repo keeps relearning ("absence of error is not success")
 * applies where silence would be mistaken for a verdict - here silence IS the
 * common verdict, because most companies have no field we have scanned enough
 * of yet.
 *
 * A failed request is swallowed on purpose. There is no action an operator
 * could take about it and no sentence worth interrupting a defect list with;
 * the comparison simply does not appear, exactly as when there is none.
 */
import { useEffect, useState } from "react";
import { getBrainPeers, type BrainPeerComparison } from "../../../../lib/siteBrain";

export function usePeerComparisons(domain: string, enabled: boolean): BrainPeerComparison[] {
  const [comparisons, setComparisons] = useState<BrainPeerComparison[]>([]);

  useEffect(() => {
    if (!enabled || !domain) {
      setComparisons([]);
      return;
    }
    let alive = true;
    getBrainPeers(domain)
      .then((res) => {
        if (alive) setComparisons(res.peers?.comparisons ?? []);
      })
      .catch(() => {
        if (alive) setComparisons([]);
      });
    return () => {
      alive = false;
    };
  }, [domain, enabled]);

  return comparisons;
}
